import {
    Box,
    chakra,
    Divider,
    Flex,
    HStack,
    Stack,
    Tag,
    Text,
    Link,
    Button,
    Collapse,
    useDisclosure,
} from '@chakra-ui/react';
import { ChevronDownIcon, ChevronUpIcon, ExternalLinkIcon } from '@chakra-ui/icons';
import { useLanguage } from '../i18n/LanguageContext';

const AnnouncementCard = ({ announcement }) => {
    const { language, t } = useLanguage();
    const { isOpen, onToggle } = useDisclosure();
    const localizedAnnouncement = language === 'en' && announcement.en
        ? { ...announcement, ...announcement.en }
        : announcement;
    const { date, tags = [], title, summary, details = [], authors, links = [] } = localizedAnnouncement;

    return (
        <Box
            bg="white"
            boxShadow="md"
            rounded="xl"
            p={{ base: 5, md: 7 }}
            textAlign="left"
            borderLeft="4px solid"
            borderColor="#437070"
        >
            <Flex
                direction={{ base: 'column', md: 'row' }}
                justify="space-between"
                align={{ base: 'flex-start', md: 'center' }}
                mb={3}
                gap={2}
            >
                <HStack spacing={2} flexWrap="wrap">
                    {tags.map((t) => (
                        <Tag
                            key={t}
                            size="sm"
                            bg="#eff5f5"
                            color="#437070"
                            fontWeight="bold"
                            border="1px solid"
                            borderColor="#9fc6c6"
                            textTransform="uppercase"
                            letterSpacing="0.5px"
                        >
                            {t}
                        </Tag>
                    ))}
                </HStack>
                <Text fontSize="sm" color="gray.500">{date}</Text>
            </Flex>

            {links.length > 0 && (
                <HStack spacing={3} flexWrap="wrap" mb={3}>
                    {links.map((l) => (
                        <Link key={l.href} href={l.href} isExternal _hover={{ textDecoration: 'none' }}>
                            <Button
                                size="sm"
                                bg="#437070"
                                color="white"
                                rightIcon={<ExternalLinkIcon />}
                                _hover={{ bg: '#70a9a9' }}
                            >
                                {l.label}
                            </Button>
                        </Link>
                    ))}
                </HStack>
            )}

            {authors && (
                <Text fontSize={{ base: 'sm', md: 'md' }} color="gray.600" mb={4}>
                    <chakra.span fontWeight="bold">{t('news.authors')}</chakra.span>{authors}
                </Text>
            )}

            {(authors || links.length > 0) && <Divider mb={4} borderColor="gray.200" />}

            <chakra.h3
                fontSize={{ base: '20px', md: '24px' }}
                fontWeight="bold"
                color="gray.700"
                mb={3}
                lineHeight="1.4"
            >
                {title}
            </chakra.h3>

            <Text fontSize={{ base: 'md', md: 'lg' }} color="gray.700" mb={4} lineHeight="1.8">
                {summary}
            </Text>

            {details.length > 0 && (
                <>
                    <Collapse in={isOpen} animateOpacity>
                        <Stack spacing={3} mb={4}>
                            {details.map((p, i) => (
                                <Text key={i} fontSize={{ base: 'md', md: 'lg' }} color="gray.700" lineHeight="1.8">
                                    {p}
                                </Text>
                            ))}
                        </Stack>
                    </Collapse>
                    <Button
                        variant="ghost"
                        size="sm"
                        color="#437070"
                        rightIcon={isOpen ? <ChevronUpIcon /> : <ChevronDownIcon />}
                        onClick={onToggle}
                        _hover={{ bg: '#eff5f5' }}
                        pl={0}
                    >
                        {isOpen ? t('news.collapse') : t('news.expand')}
                    </Button>
                </>
            )}
        </Box>
    );
};

export default AnnouncementCard;
